import Layout from "../components/Layout.tsx";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "../components/Button.tsx";
import { Badge } from "../components/Badge.tsx";
import { Dialog, DialogTitle } from "../components/Dialog.tsx";
import { Field } from "../components/Fieldset.tsx";
import { Input } from "../components/Input.tsx";
import {
  RiAddLine,
  RiPlayLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBin2Line,
  RiTimeLine,
  RiCheckLine,
  RiCloseLine,
  RiArrowRightLine,
  RiPauseLine,
  RiLoopLeftLine,
} from "@remixicon/react";
import { Text } from "../components/Text.tsx";
import {
  fetchConnections,
  fetchSyncLaunchInfo,
  postSyncRun,
  postDryRun,
  deleteConnection,
  toggleSyncPause,
  ApiError,
} from "../services/ApiService.ts";
import { Connection, SyncLaunchInfo } from "../types.ts";
import { formatSyncSchedule, useExactSystemAuthorizations } from "../hooks/data.ts";
import { toast } from "react-hot-toast";
import { useUser } from "../context/UserContext.tsx";
import { useOAuthPopupCallback } from "../hooks/useOAuthPopup";
import { parseUtcDate } from "../utils.ts";
import { isReadOnly } from "../config.ts";
import LoadingState from "../components/LoadingState.tsx";

const boekingenLabels = ["Verkoop", "Inkoop", "Loon"];
const boekingenModuleLabels: Record<string, string> = {
  verkoop: "Verkoop",
  inkoop: "Inkoop",
  loon: "Loon",
};

const formatModuleNames = (modules: string[]): string =>
  modules.map((module) => boekingenModuleLabels[module] ?? module).join(", ");

const stamgegevensLabels: { key: keyof Connection; label: string }[] = [
  { key: "enableSyncDebiteuren", label: "Debiteuren" },
  { key: "enableSyncGrootboeken", label: "Grootboeken" },
  { key: "enableSyncKostenplaatsen", label: "Kostenplaatsen" },
];

const formatCursorDate = (value: string | null): string =>
  value
    ? new Intl.DateTimeFormat("nl-NL", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(parseUtcDate(value))
    : "Nog niet gesynchroniseerd";

const toLocalDateTimeInputValue = (value: string): string => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const ConnectionsOverview = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    connection: Connection;
    type: "sync" | "dryrun";
  } | null>(null);
  const [syncLaunchInfo, setSyncLaunchInfo] = useState<SyncLaunchInfo | null>(null);
  const [syncLaunchInfoLoading, setSyncLaunchInfoLoading] = useState(false);
  const [syncLaunchInfoError, setSyncLaunchInfoError] = useState<string | null>(null);
  const [initialSyncFromByModule, setInitialSyncFromByModule] = useState<Record<string, string>>({});
  const [pauseTarget, setPauseTarget] = useState<Connection | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Connection | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notOwnTarget, setNotOwnTarget] = useState<Connection | null>(null);
  const [, setLocation] = useLocation();
  const { user } = useUser();
  const { systemAuthorizations } = useExactSystemAuthorizations(true);
  const syncLaunchRequestRef = useRef(0);

  useOAuthPopupCallback();

  const ownAuthorizationIds = new Set(
    systemAuthorizations
      .filter((authorization) => authorization.ownerUserId === user?.id)
      .map((authorization) => authorization.id)
  );

  const handleEditClick = (connection: Connection) => {
    if (connection.systemAuthorizationId && !ownAuthorizationIds.has(connection.systemAuthorizationId)) {
      setNotOwnTarget(connection);
      return;
    }
    setLocation(`/koppeling/${connection.id}`);
  };

  useEffect(() => {
    const getConnections = async () => {
      if (!user?.administrationCodes?.length) {
        toast.error("Geen administratie beschikbaar.");
        setLoading(false);
        return;
      }
      const response = await fetchConnections();
      if (response.error) {
        console.error("Failed to fetch connections:", response.error);
        setLoading(false);
        return;
      }
      setConnections(response.data || []);
      setLoading(false);
    };
    getConnections();
  }, [user?.administrationCodes]);

  const resetConfirmModal = () => {
    setConfirmAction(null);
    setSyncLaunchInfo(null);
    setSyncLaunchInfoLoading(false);
    setSyncLaunchInfoError(null);
    setInitialSyncFromByModule({});
    syncLaunchRequestRef.current += 1;
  };

  const openConfirmModal = async (connection: Connection, type: "sync" | "dryrun") => {
    setConfirmAction({ connection, type });
    setSyncLaunchInfo(null);
    setSyncLaunchInfoError(null);
    setSyncLaunchInfoLoading(true);
    setInitialSyncFromByModule({});

    const requestId = syncLaunchRequestRef.current + 1;
    syncLaunchRequestRef.current = requestId;

    try {
      const launchInfo = await fetchSyncLaunchInfo(connection.id!);
      if (syncLaunchRequestRef.current !== requestId) {
        return;
      }
      setSyncLaunchInfo(launchInfo);
    } catch (error) {
      if (syncLaunchRequestRef.current !== requestId) {
        return;
      }
      const message =
        error instanceof ApiError
          ? error.message
          : "Kon de startinformatie voor deze synchronisatie niet ophalen.";
      setSyncLaunchInfoError(message);
    } finally {
      if (syncLaunchRequestRef.current === requestId) {
        setSyncLaunchInfoLoading(false);
      }
    }
  };

  const handleConfirm = async () => {
    if (!confirmAction || !syncLaunchInfo) return;
    const { connection, type } = confirmAction;
    const connectionId = connection.id!;
    const requiredModules = syncLaunchInfo.modules.filter(
      (moduleInfo) => moduleInfo.requiresInitialSyncFrom
    );
    const missingInitialSyncModules = requiredModules
      .filter((moduleInfo) => !initialSyncFromByModule[moduleInfo.module])
      .map((moduleInfo) => boekingenModuleLabels[moduleInfo.module] ?? moduleInfo.module);

    if (missingInitialSyncModules.length > 0) {
      toast.error(`Kies een startdatum voor: ${missingInitialSyncModules.join(", ")}`);
      return;
    }

    const serializedInitialSyncFromByModule = Object.fromEntries(
      Object.entries(initialSyncFromByModule)
        .filter(([, value]) => Boolean(value))
        .map(([module, value]) => [module, new Date(value).toISOString()])
    );

    setSyncingId(connectionId);

    if (type === "sync") {
      const response = await postSyncRun(connectionId, {
        initialSyncFromByModule: serializedInitialSyncFromByModule,
      });
      setSyncingId(null);
      if (response.error) {
        toast.error(`Starten van synchronisatie mislukt: ${response.error}`);
        return;
      }
      resetConfirmModal();
      const data = response.data as { runId?: number };
      if (data?.runId) setLocation(`/synchronisatie/${data.runId}`);
      toast.success("Synchronisatie gestart");
    } else {
      const response = await postDryRun(connectionId, {
        initialSyncFromByModule: serializedInitialSyncFromByModule,
      });
      setSyncingId(null);
      if (response.error) {
        toast.error(`Starten van proefdraai mislukt: ${response.error}`);
        return;
      }
      resetConfirmModal();
      const data = response.data as { runId?: number };
      if (data?.runId) setLocation(`/proef-synchronisatie/${data.runId}`);
      toast.success("Proefdraai gestart");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const response = await deleteConnection(deleteTarget.id!);
    setDeleting(false);
    setDeleteTarget(null);
    if (response.error) {
      toast.error(`Verwijderen van koppeling mislukt: ${response.error}`);
      return;
    }
    setConnections(connections.filter((c) => c.id !== deleteTarget.id));
    toast.success("Koppeling succesvol verwijderd");
  };

  const handleTogglePause = async () => {
    if (!pauseTarget) return;
    const connectionId = pauseTarget.id!;
    const wasPaused = pauseTarget.synchronisationSchema?.isPaused;
    setToggling(true);
    const response = await toggleSyncPause(connectionId);
    setToggling(false);
    if (response.error) {
      toast.error(`Pauzeren/hervatten mislukt: ${response.error}`);
      return;
    }
    setPauseTarget(null);
    setConnections((prev) =>
      prev.map((c) =>
        c.id === connectionId
          ? {
            ...c,
            synchronisationSchema: c.synchronisationSchema
              ? { ...c.synchronisationSchema, isPaused: !c.synchronisationSchema.isPaused }
              : c.synchronisationSchema,
          }
          : c
      )
    );
    toast.success(wasPaused ? "Synchronisatie hervat" : "Synchronisatie gepauzeerd");
  };

  return (
    <Layout
      title="Koppelingen"
      description=""
      stepTitle="Overzicht van koppelingen"
      showBackButton={false}
      showNextButton={false}
    >
      <div className="flex justify-between items-center mb-6">
        <Text>
          Beheer uw koppelingen tussen Easyflex en Exact Online
        </Text>
        <Button color="primary" {...(isReadOnly ? { disabled: true } : { href: "/nieuwe-koppeling" })}>
          <RiAddLine />
          Nieuwe koppeling
        </Button>
      </div>

      {loading ? (
        <LoadingState />
      ) : connections.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">
            Nog geen koppelingen geconfigureerd
          </p>
          <Button color="primary" {...(isReadOnly ? { disabled: true } : { href: "/nieuwe-koppeling" })}>
            <RiAddLine />
            Eerste koppeling aanmaken
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connections.map((connection) => {
            const missingInitialSyncModules = connection.missingInitialSyncModules ?? [];
            const scheduleRequiresInitialSync =
              Boolean(connection.synchronisationSchema) && missingInitialSyncModules.length > 0;
            const scheduleIsPaused = connection.synchronisationSchema?.isPaused ?? false;
            const configuredScheduleBadgeColor = connection.synchronisationSchema?.dryRun ? "amber" : "blue";

            return (
              <div
                key={connection.id}
                className="overflow-hidden rounded-2xl bg-white shadow-[0px_10px_24px_rgba(15,23,42,0.08),0px_2px_6px_rgba(15,23,42,0.06)] ring-1 ring-black/5 transition-shadow hover:shadow-[0px_14px_32px_rgba(15,23,42,0.1),0px_4px_10px_rgba(15,23,42,0.06)]"
              >
                {/* Header */}
                <div className="px-5 pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      {connection.name}
                    </h3>
                    <div className="flex items-center gap-1 shrink-0">
                      {connection.synchronisationSchema && (
                        <button
                          onClick={() => setPauseTarget(connection)}
                          disabled={isReadOnly}
                          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:pointer-events-none disabled:opacity-50"
                        >
                          {scheduleIsPaused ? (
                            <><RiPlayLine className="size-3.5" />Hervatten</>
                          ) : (
                            <><RiPauseLine className="size-3.5" />Pauzeren</>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleEditClick(connection)}
                        disabled={isReadOnly}
                        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors disabled:pointer-events-none disabled:opacity-50"
                      >
                        <RiEditLine className="size-3.5" />
                        Aanpassen
                      </button>
                      <button
                        onClick={() => setDeleteTarget(connection)}
                        disabled={isReadOnly}
                        className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors disabled:pointer-events-none disabled:opacity-50"
                        title="Verwijderen"
                      >
                        <RiDeleteBin2Line className="size-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">Easyflex</p>
                      <p className="text-gray-700 font-medium">
                        {connection.sourceAdministrationName || connection.sourceAdministrationId}
                      </p>
                    </div>
                    <RiArrowRightLine className="size-4 text-gray-300 shrink-0 mt-3" />
                    <div>
                      <p className="text-xs text-gray-400">Exact Online</p>
                      <p className="text-gray-700 font-medium">
                        {connection.destinationAdministrationId}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
                    <RiTimeLine className="size-3.5 shrink-0" />
                    {connection.synchronisationSchema
                      ? formatSyncSchedule(connection.synchronisationSchema)
                      : "Geen synchronisatieschema ingesteld"}
                  </p>
                  {connection.synchronisationSchema && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {scheduleIsPaused && (
                        <Badge color="amber">
                          <RiPauseLine className="size-3" />
                          Gepauzeerd
                        </Badge>
                      )}
                      <Badge color={configuredScheduleBadgeColor}>
                        {connection.synchronisationSchema.dryRun
                          ? <><RiEyeLine className="size-3" />Proefdraai</>
                          : "Synchronisatie"}
                      </Badge>
                    </div>
                  )}
                  {scheduleRequiresInitialSync && (
                    <p className="mt-3 text-xs text-amber-700">
                      Voer eerst een succesvolle handmatige synchronisatie uit voor {formatModuleNames(missingInitialSyncModules)} voordat
                      de automatische synchronisatie start.
                    </p>
                  )}
                </div>

                {/* Sync configuration */}
                <div className="px-5 pt-4 pb-5 space-y-3">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Boekingen
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {boekingenLabels.map((label) => {
                      const enabled = connection.mappings
                        ?.filter((m) => m.type === "journal")
                        .some((m) => m.source.toLowerCase().includes(label.toLowerCase()));
                      return (
                        <span
                          key={label}
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${enabled
                            ? "bg-green-100 text-green-700"
                            : "bg-red-50 text-red-600"
                            }`}
                        >
                          {enabled ? (
                            <RiCheckLine className="size-3" />
                          ) : (
                            <RiCloseLine className="size-3" />
                          )}
                          {label}
                        </span>
                      );
                    })}
                  </div>

                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Stamgegevens
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {stamgegevensLabels.map((m) => {
                      const enabled = connection[m.key as keyof Connection] === true;
                      return (
                        <span
                          key={m.key}
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${enabled
                            ? "bg-green-100 text-green-700"
                            : "bg-red-50 text-red-600"
                            }`}
                        >
                          {enabled ? (
                            <RiCheckLine className="size-3" />
                          ) : (
                            <RiCloseLine className="size-3" />
                          )}
                          {m.label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 border-t border-gray-200 bg-gray-50/70 px-5 py-4">
                  <Button
                    outline
                    className="flex-1"
                    onClick={() => openConfirmModal(connection, "dryrun")}
                    disabled={isReadOnly}
                  >
                    <RiEyeLine />
                    Proefdraai
                  </Button>
                  <Button
                    color="primary"
                    className="flex-1"
                    onClick={() => openConfirmModal(connection, "sync")}
                    disabled={isReadOnly}
                  >
                    <RiLoopLeftLine className="!size-[1.125rem] sm:!size-[1.125rem]" />
                    Synchroniseren
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation dialog */}
      <Dialog
        open={confirmAction !== null}
        onClose={resetConfirmModal}
        className="max-w-lg"
      >
        {confirmAction && (
          <>
            {/* Header with icon */}
            <div className="flex items-center gap-3">
              <div
                className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${confirmAction.type === "dryrun"
                  ? "bg-gray-100"
                  : "bg-blue-50"
                  }`}
              >
                {confirmAction.type === "dryrun" ? (
                  <RiEyeLine className="size-5 text-gray-600" />
                ) : (
                  <RiPlayLine className="size-5 text-blue-600" />
                )}
              </div>
              <div>
                <DialogTitle className="text-base font-semibold text-gray-900">
                  {confirmAction.type === "dryrun"
                    ? "Proefdraai starten"
                    : "Synchronisatie starten"}
                </DialogTitle>
                <p className="text-xs text-gray-500">
                  {confirmAction.type === "dryrun"
                    ? "Bekijk welke gegevens gesynchroniseerd worden"
                    : "Gegevens worden direct geboekt in Exact Online"}
                </p>
              </div>
            </div>

            {/* Connection context */}
            <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">
                {confirmAction.connection.name}
              </p>
              <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
                <span>
                  Easyflex{" "}
                  {confirmAction.connection.sourceAdministrationName ||
                    confirmAction.connection.sourceAdministrationId}
                </span>
                <RiArrowRightLine className="size-3 text-gray-300" />
                <span>Exact Online</span>
              </div>
              <p className="mt-3 text-xs text-gray-700 border-t border-gray-200 pt-3">
                {syncLaunchInfo && syncLaunchInfo.modules.some((m) => m.requiresInitialSyncFrom)
                  ? `Alle nieuwe en gewijzigde posten sinds de laatste synchronisatie worden opgehaald binnen de laatste ${syncLaunchInfo.lookbackWeeks} weken. Voor modules die nog niet eerder zijn gesynchroniseerd is een startdatum nodig.`
                  : syncLaunchInfo
                    ? `Alle nieuwe en gewijzigde posten sinds de laatste synchronisatie worden opgehaald binnen de laatste ${syncLaunchInfo.lookbackWeeks} weken.`
                    : "Alle nieuwe en gewijzigde posten sinds de laatste synchronisatie worden opgehaald."}
              </p>
            </div>

            {syncLaunchInfoLoading ? (
              <div className="mt-2 text-sm text-gray-500">
                Moduleinformatie laden...
              </div>
            ) : syncLaunchInfoError ? (
              <div className="mt-2 text-sm text-red-600">
                {syncLaunchInfoError}
              </div>
            ) : syncLaunchInfo && syncLaunchInfo.modules.length > 0 ? (
              <div className="mt-2 divide-y divide-gray-100">
                {syncLaunchInfo.modules.map((moduleInfo) => (
                  <div key={moduleInfo.module} className="py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        {boekingenModuleLabels[moduleInfo.module] ?? moduleInfo.module}
                      </p>
                      <span className={`inline-block size-2 rounded-full shrink-0 ${moduleInfo.requiresInitialSyncFrom ? "bg-amber-400" : "bg-green-500"}`} />
                    </div>
                    {moduleInfo.requiresInitialSyncFrom ? (
                      <Field className="mt-1">
                        <p className="text-xs text-gray-500">
                          Nog niet eerder succesvol gesynchroniseerd. Kies vanaf welke datum wijzigingen uit Easyflex moeten worden meegenomen.
                        </p>
                        <div className="mt-2">
                          <Input
                            id={`initial-sync-from-${moduleInfo.module}`}
                            type="datetime-local"
                            value={initialSyncFromByModule[moduleInfo.module] ?? ""}
                            onChange={(event) =>
                              setInitialSyncFromByModule((currentValues) => ({
                                ...currentValues,
                                [moduleInfo.module]: event.target.value,
                              }))
                            }
                            max={toLocalDateTimeInputValue(new Date().toISOString())}
                          />
                        </div>
                      </Field>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        Laatst gesynchroniseerd: {formatCursorDate(moduleInfo.lastCursorAt)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 text-sm text-gray-500">
                Geen boekingenmodules gevonden voor deze koppeling.
              </div>
            )}

            {/* Info/warning banner */}
            {confirmAction.type === "dryrun" ? (
              <div className="mt-4 -mx-6 border-t border-blue-100 bg-blue-50 px-6 py-3">
                <p className="text-xs text-blue-700 leading-relaxed">
                  Dit is een proefdraai. De opgegeven startdatums worden alleen voor deze proefdraai gebruikt en hebben geen invloed op toekomstige synchronisaties.
                </p>
              </div>
            ) : (
              <div className="mt-4 -mx-6 border-t border-amber-100 bg-amber-50 px-6 py-3">
                <p className="text-xs text-amber-800 leading-relaxed">
                  Gegevens worden <strong>direct geboekt</strong> in
                  Exact Online. Voor modules zonder eerdere synchronisatie worden alle wijzigingen vanaf de opgegeven startdatum meegenomen binnen de ingestelde terugkijkperiode.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex gap-3 justify-end">
              <button
                onClick={resetConfirmModal}
                disabled={syncingId !== null}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Annuleren
              </button>
              <Button
                color={confirmAction.type === "dryrun" ? "dark" : "primary"}
                onClick={handleConfirm}
                disabled={syncingId !== null || syncLaunchInfoLoading || syncLaunchInfoError !== null}
              >
                {syncingId !== null
                  ? "Bezig..."
                  : confirmAction.type === "dryrun"
                    ? "Start proefdraai"
                    : "Start synchronisatie"}
              </Button>
            </div>
          </>
        )}
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
      >
        {deleteTarget && (
          <>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Koppeling verwijderen
            </DialogTitle>

            <div className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <span className="font-medium">{deleteTarget.name}</span>
            </div>

            <p className="mt-3 text-sm text-gray-600">
              Weet u zeker dat u deze koppeling wilt verwijderen? Dit kan niet
              ongedaan worden gemaakt.
            </p>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Annuleren
              </button>
              <Button
                color="red"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Bezig..." : "Verwijderen"}
              </Button>
            </div>
          </>
        )}
      </Dialog>

      {/* Not-your-authentication blocking dialog */}
      <Dialog
        open={notOwnTarget !== null}
        onClose={() => setNotOwnTarget(null)}
      >
        {notOwnTarget && (
          <>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Aanpassen niet mogelijk
            </DialogTitle>

            <div className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="font-medium">{notOwnTarget.name}</span>
            </div>

            <p className="mt-3 text-sm text-gray-600">
              Je kunt deze koppeling niet aanpassen: de Exact Online-authenticatie
              hoort niet bij jou, maar bij een andere gebruiker.
            </p>

            <div className="mt-6 flex justify-end">
              <Button color="primary" onClick={() => setNotOwnTarget(null)}>
                Sluiten
              </Button>
            </div>
          </>
        )}
      </Dialog>

      {/* Pause/resume confirmation dialog */}
      <Dialog
        open={pauseTarget !== null}
        onClose={() => setPauseTarget(null)}
      >
        {pauseTarget && (
          <>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {pauseTarget.synchronisationSchema?.isPaused
                ? "Synchronisatie hervatten"
                : "Synchronisatie pauzeren"}
            </DialogTitle>

            <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-700">
              <span className="font-medium">{pauseTarget.name}</span>
            </div>

            <p className="mt-3 text-sm text-gray-600">
              {pauseTarget.synchronisationSchema?.isPaused
                ? `De automatische synchronisatie wordt hervat volgens het ingestelde schema: ${formatSyncSchedule(pauseTarget.synchronisationSchema)}.`
                : "De automatische synchronisatie wordt gepauzeerd. Handmatige synchronisaties blijven mogelijk."}
            </p>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setPauseTarget(null)}
                disabled={toggling}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Annuleren
              </button>
              <Button
                color="primary"
                onClick={handleTogglePause}
                disabled={toggling}
              >
                {toggling
                  ? "Bezig..."
                  : pauseTarget.synchronisationSchema?.isPaused
                    ? "Hervatten"
                    : "Pauzeren"}
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </Layout>
  );
};

export default ConnectionsOverview;
